import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import PageHeader from "../../../../components/PageHeader";
import Loading from "../../../../components/utils/Loading";
import SettingsCreateWizardLayout from "../../SettingsCreateWizardLayout";
import SettingsWizardFooter from "../../SettingsWizardFooter";
import AddressLocationTab, {
  AddressGeocodeInput,
  suggest,
} from "../../../address/AddressLocationTab";
import OpeningHoursEditor, {
  computeUtcOpening,
  utcToLocalOpening,
} from "../../../address/OpeningHoursEditor";
import Outlet from "../../../address/Outlet";
import { handleFormChange } from "../../../../components/modals/EditableTable";
import { formattedError } from "../../../../components/formatted/FormattedText";
import DeleteModal from "../../../../components/modals/DeleteModal";

const defaultData = {
  name: "",
  role: "",
  number: "",
  street: "",
  postalCode: "",
  city: "",
  country: "",
  location: [0, 0],
  opening: { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] },
  openingTimezone: "Europe/Paris",
  outlet: {
    landfill: [],
    energyRecovery: [],
    materialRecovery: [],
  },
};

const ensureAddressShape = (site) => ({
  ...defaultData,
  ...site,
  postalCode: site?.postalCode ?? site?.postalcode ?? "",
  opening: site?.opening || defaultData.opening,
  openingTimezone:
    site?.openingTimezone || site?.timezone || defaultData.openingTimezone,
  outlet: site?.outlet || defaultData.outlet,
});

const buildApiPayload = (fd) => {
  const postalRaw = fd.postalCode ?? fd.postalcode ?? "";
  const postalDigits = String(postalRaw).replace(/\s/g, "");
  const postalNum = parseInt(postalDigits, 10);
  const postalCode =
    postalDigits && Number.isFinite(postalNum) ? postalNum : postalRaw;

  return {
    _id: fd._id,
    name: (fd.name || "").trim(),
    number: fd.number != null && fd.number !== "" ? String(fd.number) : "",
    street: (fd.street || "").trim(),
    postalCode,
    city: (fd.city || "").trim(),
    country: fd.country != null ? String(fd.country) : "",
    role: fd.role,
    location: Array.isArray(fd.location) ? fd.location : [0, 0],
    opening: fd.opening || defaultData.opening,
    outlet: fd.outlet || defaultData.outlet,
    timezone: fd.openingTimezone || "Europe/Paris",
  };
};

const hasValidMapLocation = (location) => {
  if (!Array.isArray(location) || location.length < 2) return false;
  const lat = Number(location[0]);
  const lng = Number(location[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  return !(lat === 0 && lng === 0);
};

/** Affichage maquette : « 11 rue …, 31150 Ville » */
const formatSiteAddressLine = (d) => {
  const num =
    d?.number != null && d.number !== "" ? String(d.number).trim() : "";
  const street = (d?.street || "").trim();
  const pc =
    d?.postalCode !== undefined && d?.postalCode !== null && d.postalCode !== ""
      ? String(d.postalCode).trim()
      : "";
  const city = (d?.city || "").trim();
  const left = [num, street].filter(Boolean).join(" ");
  const right = [pc, city].filter(Boolean).join(" ");
  if (left && right) return `${left}, ${right}`;
  return left || right || "";
};

/**
 * Découpe une ligne saisie en champs structurés.
 * CP + ville sont lus en fin de chaîne (plusieurs virgules possibles avant).
 */
const parseSiteAddressLine = (line) => {
  const trimmed = (line || "").trim();
  if (!trimmed) return { number: "", street: "", postalCode: "", city: "" };

  let postalCode = "";
  let city = "";
  let remainder = trimmed;

  const endMatch = trimmed.match(/,?\s*(\d{4,5})\s+(.+)$/);
  if (endMatch) {
    postalCode = endMatch[1];
    city = endMatch[2].trim();
    remainder = trimmed.slice(0, endMatch.index).replace(/,\s*$/, "").trim();
  } else if (!trimmed.includes(",")) {
    const m = trimmed.match(/^(\d+[A-Za-z]?)\s+(.+)$/);
    if (m) {
      return { number: m[1], street: m[2].trim(), postalCode: "", city: "" };
    }
    return { number: "", street: trimmed, postalCode: "", city: "" };
  } else {
    const parts = trimmed
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      const last = parts[parts.length - 1];
      const lastAsCpCity = last.match(/^(\d{4,5})\s+(.+)$/);
      if (lastAsCpCity) {
        postalCode = lastAsCpCity[1];
        city = lastAsCpCity[2].trim();
        remainder = parts.slice(0, -1).join(", ");
      } else {
        city = last;
        remainder = parts.slice(0, -1).join(", ");
      }
    }
  }

  const mNum = remainder.match(/^(\d+[A-Za-z]?)\s+(.+)$/);
  if (mNum) {
    return { number: mNum[1], street: mNum[2].trim(), postalCode, city };
  }
  return { number: "", street: remainder, postalCode, city };
};

/** Complète le géocodage avec la saisie manuelle (voie complète, lieu-dit, etc.). */
const mergeAddressFields = (fromGeocoder, fromParsed = {}) => {
  const geocoderStreet = (fromGeocoder?.street || "").trim();
  const parsedStreet = (fromParsed.street || "").trim();
  const street =
    parsedStreet.length > geocoderStreet.length
      ? parsedStreet
      : geocoderStreet || parsedStreet;

  return {
    number: fromGeocoder?.number || fromParsed.number || "",
    street,
    postalCode: fromGeocoder?.postalCode || fromParsed.postalCode || "",
    city: (fromGeocoder?.city || "").trim() || (fromParsed.city || "").trim(),
    country: fromGeocoder?.country || fromParsed.country || "",
  };
};

/** Validations obligatoires de l'étape Infos générales (partagées save + Continuer). */
const validateGeneralForm = (mergedForm, t) => {
  const payload = buildApiPayload(mergedForm);

  if (!payload.name) {
    toast.error(t("settingsSites.siteNameRequired"), {
      className: "custom-toast",
    });
    return false;
  }
  if (!payload.role) {
    toast.error(t("settingsSites.siteRoleRequired"), {
      className: "custom-toast",
    });
    return false;
  }
  if (!payload.street) {
    toast.error(t("settingsSites.streetRequired"), {
      className: "custom-toast",
    });
    return false;
  }
  if (
    !String(payload.postalCode ?? "")
      .toString()
      .trim()
  ) {
    toast.error(t("settingsSites.postalCodeRequired"), {
      className: "custom-toast",
    });
    return false;
  }
  if (!payload.city) {
    toast.error(t("settingsSites.cityRequired"), {
      className: "custom-toast",
    });
    return false;
  }
  if (!hasValidMapLocation(mergedForm.location)) {
    toast.error(t("settingsSites.locationRequired"), {
      className: "custom-toast",
    });
    return false;
  }
  return true;
};

const validateStep = (stepKey, mergedForm, t) => {
  if (stepKey === "general") return validateGeneralForm(mergedForm, t);
  return true;
};

const normalizeForDirtyCheck = (data) => {
  const safe = data || {};
  return {
    name: safe.name || "",
    role: safe.role || "",
    number: safe.number || "",
    street: safe.street || "",
    postalCode: safe.postalCode || safe.postalcode || "",
    city: safe.city || "",
    country: safe.country || "",
    location: Array.isArray(safe.location) ? safe.location : [0, 0],
    opening: safe.opening || {
      0: [],
      1: [],
      2: [],
      3: [],
      4: [],
      5: [],
      6: [],
    },
    openingTimezone: safe.openingTimezone || "Europe/Paris",
    outlet: safe.outlet || {
      landfill: [],
      energyRecovery: [],
      materialRecovery: [],
    },
  };
};

/** Largeur commune des panneaux Infos générales et Horaires d'ouverture */
const SITE_EDITOR_PANEL_CARD =
  "w-[640px] overflow-hidden rounded-lg border border-[#d3d3d3] bg-white";
const SITE_EDITOR_PANEL_CARD_GENERAL =
  "w-[640px] rounded-lg border border-[#d3d3d3] bg-white";
/** Exutoire pro / ménagers — même largeur fixe ; pas d'overflow-hidden (en-tête sticky). */
const SITE_EDITOR_TREATMENT_PANEL_CARD =
  "w-[1024px] max-w-full shrink-0 rounded-lg border border-[#d3d3d3] bg-white";
const SITE_EDITOR_INPUT_CLASS =
  "w-full border border-[#d3d3d3] rounded px-3 py-2 h-10 outline-none focus:border-[#00A7A6]";

const SettingsSiteEditor = ({ token, API, mode = "edit" }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(mode === "edit");
  const [formData, setFormData] = useState(defaultData);
  const [initialSnapshot, setInitialSnapshot] = useState(
    JSON.stringify(normalizeForDirtyCheck(defaultData)),
  );
  const [tab, setTab] = useState("general");
  const [maxReachedStepIndex, setMaxReachedStepIndex] = useState(0);
  const [siteLoadError, setSiteLoadError] = useState(null);
  /** Saisie en cours dans le champ adresse unique (null = afficher la valeur dérivée du formulaire) */
  const [siteAddressLineDraft, setSiteAddressLineDraft] = useState(null);
  const [mapRecenterToken, setMapRecenterToken] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const roleOptions = useMemo(
    () => [
      { value: "", label: t("Choose a role") },
      { value: "sorting_plant", label: t("Sorting plant") },
      { value: "client", label: t("Client") },
      { value: "building_site", label: t("Building site") },
      { value: "dump", label: t("Dump") },
      { value: "industrial_site", label: t("Industrial site") },
      { value: "landfill_site", label: t("Landfill site") },
      { value: "incineration_centre", label: t("Incineration centre") },
      { value: "engineering_consultancy", label: t("Engineering consultancy") },
    ],
    [t],
  );

  const steps = useMemo(
    () => [
      { key: "general", label: t("General information") },
      { key: "hours", label: t("Opening hours") },
      {
        key: "outlet",
        label: t("settingsSites.professionalTreatment"),
      },
      {
        key: "hwOutlet",
        label: t("settingsSites.householdTreatment"),
      },
    ],
    [t],
  );
  const headerTabs = steps.map((item) => ({ id: item.key, label: item.label }));

  const roleOptionsCreate = useMemo(
    () => [
      { value: "", label: t("settingsSites.selectRole") },
      ...roleOptions.slice(1),
    ],
    [roleOptions, t],
  );

  const materials = useMemo(
    () => JSON.parse(localStorage.getItem("materialsDescription")) || [],
    [],
  );

  const vmData = useMemo(() => {
    const tz = formData?.openingTimezone || "Europe/Paris";
    const openingUtc =
      formData && typeof formData.opening === "object" ? formData.opening : {};
    const openingLocal = utcToLocalOpening(openingUtc, tz);
    return { ...formData, openingLocal };
  }, [formData]);
  const hasUnsavedChanges = useMemo(() => {
    const current =
      siteAddressLineDraft != null
        ? { ...formData, ...parseSiteAddressLine(siteAddressLineDraft) }
        : formData;
    return JSON.stringify(normalizeForDirtyCheck(current)) !== initialSnapshot;
  }, [formData, siteAddressLineDraft, initialSnapshot]);

  const setDataFromChild = (updater) => {
    setFormData((prev) => {
      const next =
        typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      const tz =
        next?.openingTimezone || prev?.openingTimezone || "Europe/Paris";
      if (next && next.openingLocal) {
        const utc = computeUtcOpening(next.openingLocal, tz);
        delete next.openingLocal;
        next.opening = utc;
        next.openingTimezone = tz;
      }
      return next;
    });
  };

  /** Carte / géocodage : conserve la voie saisie si le reverse geocode est moins précis */
  const setDataFromMap = (updater) => {
    const draftParsed =
      siteAddressLineDraft != null
        ? parseSiteAddressLine(siteAddressLineDraft)
        : null;
    setSiteAddressLineDraft(null);
    setDataFromChild((prev) => {
      const next =
        typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      if (!draftParsed) return next;
      const merged = mergeAddressFields(next, draftParsed);
      return { ...next, ...merged };
    });
  };

  const applyGeocodePick = (it, parsedFallback = null) => {
    if (!it) return;
    const draftParsed =
      parsedFallback ??
      (siteAddressLineDraft != null
        ? parseSiteAddressLine(siteAddressLineDraft)
        : null);
    setSiteAddressLineDraft(null);
    const merged = mergeAddressFields(it, draftParsed || {});
    setDataFromChild((prev) => ({
      ...prev,
      number: merged.number ? String(merged.number) : "",
      street: merged.street || "",
      city: merged.city || "",
      postalCode: merged.postalCode || "",
      country: merged.country || prev.country || "",
      location: [
        Number.isFinite(it.lat) ? it.lat : prev.location?.[0] || 0,
        Number.isFinite(it.lng) ? it.lng : prev.location?.[1] || 0,
      ],
    }));
    setMapRecenterToken((n) => n + 1);
  };

  const commitAddressDraft = async () => {
    if (siteAddressLineDraft === null) return;
    const parsed = parseSiteAddressLine(siteAddressLineDraft);
    const q = siteAddressLineDraft.trim();
    if (q.length >= 2) {
      try {
        const results = await suggest(q);
        if (results[0]) {
          applyGeocodePick(results[0], parsed);
          return;
        }
      } catch {
        /* repli parsing manuel */
      }
    }
    setDataFromChild((prev) => ({ ...prev, ...parsed }));
    setSiteAddressLineDraft(null);
  };

  const goToStep = async (key) => {
    if (tab === "general") await commitAddressDraft();
    setTab(key);
  };

  const goToCreateStep = (_index, step) => {
    goToStep(step.key);
  };

  const goToNextStep = () => {
    const idx = steps.findIndex((s) => s.key === tab);
    if (idx < 0 || idx >= steps.length - 1) return;

    const mergedForValidation =
      siteAddressLineDraft != null
        ? { ...formData, ...parseSiteAddressLine(siteAddressLineDraft) }
        : formData;

    if (!validateStep(tab, mergedForValidation, t)) return;

    goToStep(steps[idx + 1].key);
    setMaxReachedStepIndex((prev) => Math.max(prev, idx + 1));
  };

  useEffect(() => {
    if (mode !== "edit" || !token || !id) {
      if (mode === "edit") {
        setIsLoading(false);
        if (!id) setSiteLoadError("not_found");
      }
      return undefined;
    }
    let cancelled = false;
    const fetchSite = async () => {
      setIsLoading(true);
      setSiteLoadError(null);
      try {
        const response = await axios.get(`${API}address/${id}`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const raw = response?.data?.data;
        if (raw && raw._id) {
          const normalized = ensureAddressShape(raw);
          setFormData(normalized);
          setInitialSnapshot(
            JSON.stringify(normalizeForDirtyCheck(normalized)),
          );
        } else {
          setSiteLoadError("not_found");
        }
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        setSiteLoadError(status === 404 ? "not_found" : "error");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchSite();
    return () => {
      cancelled = true;
    };
  }, [mode, token, API, id]);

  const saveSite = async () => {
    const token = Cookies.get("userToken");
    if (mode === "edit" && !formData?._id) {
      toast.error(t("settingsSites.siteNotFound"), {
        className: "custom-toast",
      });
      return null;
    }

    const mergedForm =
      siteAddressLineDraft != null
        ? { ...formData, ...parseSiteAddressLine(siteAddressLineDraft) }
        : formData;

    if (!validateGeneralForm(mergedForm, t)) return null;

    const payload = buildApiPayload(mergedForm);

    try {
      if (mode === "edit") {
        const response = await axios.post(
          `${API}address/update/${payload._id}`,
          payload,
          {
            headers: { authorization: `Bearer ${token}` },
          },
        );
        if (response?.status === 200) {
          setSiteAddressLineDraft(null);
          const next = {
            ...mergedForm,
            ...payload,
            openingTimezone: payload.timezone,
          };
          setFormData(next);
          setInitialSnapshot(JSON.stringify(normalizeForDirtyCheck(next)));
          toast.success(t("settingsSites.siteUpdatedSuccess"), {
            className: "custom-toast",
          });
        }
        return response;
      }

      const response = await axios.post(`${API}address/add`, payload, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (response?.status === 200) {
        setSiteAddressLineDraft(null);
        const created = response.data;
        const next = ensureAddressShape({
          ...mergedForm,
          ...payload,
          _id: created?._id,
          openingTimezone: payload.timezone,
        });
        setFormData(next);
        setInitialSnapshot(JSON.stringify(normalizeForDirtyCheck(next)));
        toast.success(t("settingsSites.siteCreatedSuccess"), {
          className: "custom-toast",
        });
      }
      return response;
    } catch (error) {
      const msg = formattedError(error.response?.data?.error);
      toast.error(msg, { className: "custom-toast" });
      return error.response ?? null;
    }
  };

  const renderGeneral = () => (
    <div className={`${SITE_EDITOR_PANEL_CARD_GENERAL} pt-6 pb-8`}>
      <div className="px-8">
        <h3 className="font-extrabold text-[24px] leading-[32px] mb-6">
          {t("General information")}
        </h3>
      </div>
      <div className="px-8 pb-2">
        <div className="mb-4">
          <label className="block mb-1 text-[14px] leading-[22px] font-semibold">
            {t("settingsSites.siteName")}{" "}
            <span className="text-[#AF2F3C]">*</span>
          </label>
          <input
            type="text"
            className={SITE_EDITOR_INPUT_CLASS}
            value={vmData.name}
            onChange={(event) =>
              handleFormChange(event, vmData, setDataFromChild)
            }
            name="name"
          />
        </div>
        <div className="mb-2">
          <label className="block mb-1 text-[14px] leading-[22px] font-semibold">
            {t("Role")} <span className="text-[#AF2F3C]">*</span>
          </label>
          <div className="relative">
            <select
              className={`${SITE_EDITOR_INPUT_CLASS} appearance-none bg-white pr-10 text-[14px] text-[#222] cursor-pointer`}
              value={vmData.role}
              onChange={(event) =>
                handleFormChange(event, vmData, setDataFromChild)
              }
              name="role"
            >
              {(mode === "create" ? roleOptionsCreate : roleOptions).map(
                (role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ),
              )}
            </select>
            <div
              className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
              aria-hidden
            >
              <svg
                className="h-4 w-4 text-[#222]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-[#d3d3d3] mt-2" />
      <div className="px-8 pt-4">
        <label className="block mb-1 text-[14px] leading-[22px] font-semibold">
          {t("settingsSites.siteAddress")}{" "}
          <span className="text-[#AF2F3C]">*</span>
        </label>
        <div className="relative z-[2] mb-3 overflow-visible">
          <AddressGeocodeInput
            variant="site-field"
            inputId="site-address-line"
            value={siteAddressLineDraft ?? formatSiteAddressLine(vmData)}
            onChange={(v) => setSiteAddressLineDraft(v)}
            onFocus={() =>
              setSiteAddressLineDraft(
                (prev) => prev ?? formatSiteAddressLine(vmData),
              )
            }
            onPick={applyGeocodePick}
            onBlur={() => {
              void commitAddressDraft();
            }}
            placeholder={t("settingsSites.siteAddressPlaceholder")}
          />
        </div>
        <div className="relative z-0">
          <AddressLocationTab
            editFormData={vmData}
            setEditFormData={setDataFromMap}
            handleFormChange={handleFormChange}
            mapHeight="334px"
            showSearch={false}
            showMapOverlay={false}
            recenterToken={mapRecenterToken}
          />
        </div>
      </div>
    </div>
  );

  const renderHours = () => (
    <div className={SITE_EDITOR_PANEL_CARD}>
      <div className="px-8 pt-6">
        <h3 className="mb-6 text-[24px] font-extrabold leading-[32px]">
          {t("Opening hours")}
        </h3>
      </div>
      <OpeningHoursEditor
        variant="site-settings"
        value={vmData.openingLocal}
        timezone={vmData.openingTimezone || "Europe/Paris"}
        onChange={({ openingLocal, timezone }) =>
          setDataFromChild((prev) => ({
            ...prev,
            openingLocal,
            openingTimezone: timezone,
          }))
        }
      />
    </div>
  );

  const renderOutlet = (household = false) => {
    const panelTitle =
      mode === "create" && !household
        ? t("settingsSites.chooseOutlet")
        : household
          ? t("settingsSites.householdTreatment")
          : t("settingsSites.professionalTreatment");

    return (
      <div className={SITE_EDITOR_TREATMENT_PANEL_CARD}>
        <div className="px-8 pt-6 pb-3">
          <h3 className="font-extrabold text-[24px] leading-[32px]">
            {panelTitle}
          </h3>
        </div>
        <div className="px-0 pb-6">
          <Outlet
            editFormData={vmData}
            setEditFormData={setDataFromChild}
            materials={materials.filter((m) =>
              household
                ? m.referenceName.includes("hw_")
                : !m.referenceName.includes("hw_"),
            )}
            activeTab={household ? "hwOutlet" : "outlet"}
            targetAddress=""
            variant="site-treatment-table"
          />
        </div>
      </div>
    );
  };

  const currentTabIndex = steps.findIndex((item) => item.key === tab);
  const currentTabContent = {
    general: renderGeneral(),
    hours: renderHours(),
    outlet: renderOutlet(false),
    hwOutlet: renderOutlet(true),
  }[tab];
  const isLastTab = currentTabIndex === steps.length - 1;
  const isEditMode = mode === "edit";
  const isCreateMode = mode === "create";
  const showFooter = isCreateMode || hasUnsavedChanges;
  const contentBottomPadding = showFooter ? "pb-28" : "pb-6";

  const handleDelete = async () => {
    try {
      const authToken = Cookies.get("userToken");
      await axios.delete(`${API}address/delete/${id}`, {
        headers: { authorization: `Bearer ${authToken}` },
      });
      toast.success(t("settingsSites.siteDeletedSuccess"), {
        className: "custom-toast",
      });
      history.push("/settings/sites");
    } catch (error) {
      console.error(error);
      toast.error(formattedError(error.response?.data?.error), {
        className: "custom-toast",
      });
    }
  };

  const actionsMenuItems =
    isEditMode && formData?._id && !siteLoadError
      ? [
          {
            label: t("settingsSites.deleteSite"),
            icon: <i className="far fa-trash-alt text-[#222222]" aria-hidden />,
            onClick: () => setShowDeleteModal(true),
          },
        ]
      : null;

  if (isLoading) return <Loading color="#F5F5F5" />;

  if (isEditMode && siteLoadError) {
    return (
      <div className="settings-page-wrapper">
        <PageHeader
          title={t("settingsSites.siteTitleFallback")}
          showBackButton={true}
          backRoute="/settings/sites"
          backLabel={t("settingsSites.backToSites")}
          tabs={[]}
        />
        <div className="settings-content-wrapper flex justify-center items-center py-16 px-4">
          <div className="text-center max-w-md">
            <p className="text-lg text-[#222] mb-6">
              {siteLoadError === "not_found"
                ? t("settingsSites.siteNotFound")
                : t("settingsSites.loadSiteError")}
            </p>
            <button
              type="button"
              className="modal-button modal-button-save"
              onClick={() => history.push("/settings/sites")}
            >
              {t("settingsSites.backToSites")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const editorBody = (
    <div className="flex w-full justify-center">{currentTabContent}</div>
  );

  return (
    <div
      className={`settings-page-wrapper${isCreateMode ? " settings-page-wrapper--wizard" : ""}`}
    >
      {isCreateMode ? (
        <>
          <PageHeader
            title={t("settingsSites.createNewSite")}
            showBackButton={true}
            backRoute="/settings/sites"
            backLabel={t("settingsSites.quit")}
            tabs={[]}
          />
          <SettingsCreateWizardLayout
            steps={steps}
            currentIndex={currentTabIndex}
            maxReachedIndex={maxReachedStepIndex}
            onStepClick={goToCreateStep}
            ariaLabel="Site creation steps"
          >
            {editorBody}
          </SettingsCreateWizardLayout>
        </>
      ) : (
        <>
          <PageHeader
            title={formData?.name || t("settingsSites.siteTitleFallback")}
            showBackButton={true}
            backRoute="/settings/sites"
            backLabel={t("settingsSites.backToSites")}
            tabs={headerTabs}
            activeTab={tab}
            onTabChange={goToStep}
            actionsMenuItems={actionsMenuItems}
          />
          <div className={`settings-content-wrapper ${contentBottomPadding}`}>
            <div className="mx-auto max-w-[1024px] px-8">{editorBody}</div>
          </div>
        </>
      )}
      {showFooter &&
        (isCreateMode ? (
          <SettingsWizardFooter
            stepIndex={currentTabIndex}
            onBack={() => goToStep(steps[currentTabIndex - 1].key)}
            onQuit={async () => {
              await commitAddressDraft();
              if (!hasUnsavedChanges) {
                history.push("/settings/sites");
                return;
              }
              const response = await saveSite();
              if (response?.status === 200) {
                history.push("/settings/sites");
              }
            }}
            quitLabel={
              hasUnsavedChanges
                ? t("settingsSites.quitAndSave")
                : t("settingsSites.quit")
            }
            primaryLabel={
              isLastTab ? t("settingsSites.createTheSite") : t("Continue")
            }
            onPrimary={
              isLastTab
                ? async () => {
                    const response = await saveSite();
                    if (response?.status === 200) {
                      history.push("/settings/sites");
                    }
                  }
                : goToNextStep
            }
          />
        ) : (
          <div className="settings-wizard-footer">
            <div
              className="settings-wizard-footer__inner"
              style={{ maxWidth: 1024 }}
            >
              {isEditMode ? (
                <>
                  <button
                    type="button"
                    className="modal-button modal-button-cancel"
                    onClick={() => history.push("/settings/sites")}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="button"
                    className="modal-button modal-button-save"
                    onClick={async () => {
                      const response = await saveSite();
                      if (response?.status === 200) {
                        history.push("/settings/sites");
                      }
                    }}
                  >
                    {t("Save")}
                  </button>
                </>
              ) : null}
            </div>
          </div>
        ))}

      {showDeleteModal && (
        <DeleteModal
          modal={showDeleteModal}
          setModal={setShowDeleteModal}
          id={formData._id || id}
          message1={t("settingsSites.deleteConfirm", {
            name: formData?.name || "",
          })}
          message2={formData?.name || ""}
          messageValidation={t("settingsSites.siteDeletedSuccess")}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default SettingsSiteEditor;
